import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { collectAllTags } from "../../../shared/stepOrchestration";
import { newId } from "../../api";
import { useEditor, type TabKey } from "../DemoEditor";
import { starterSurvey } from "./starterSurvey";
import TagAutocompleteInput from "./TagAutocompleteInput";
import { move, useSurveyDraft } from "./useSurveyDraft";

const TYPE_LABEL: Record<string, string> = {
  single_select: "Pick one",
  multi_select: "Pick any",
  short_text: "Short text answer",
};

export default function SurveyTab() {
  const { bundle, go } = useEditor();
  const [params] = useSearchParams();
  const { cfg, setCfg, save, dirty, saving, msg } = useSurveyDraft();
  const qs = cfg.questions;
  const setQs = (questions: typeof qs) => setCfg({ ...cfg, questions });
  const patchQ = (i: number, p: Partial<(typeof qs)[number]>) => setQs(qs.map((q, n) => (n === i ? { ...q, ...p } : q)));

  const allKnownTags = useMemo(
    () => collectAllTags(cfg.questions, bundle.outcomes),
    [cfg.questions, bundle.outcomes]
  );

  const addQuestion = () => setCfg({ ...cfg, surveyEnabled: true, questions: [...qs, {
    id: newId(), prompt: "", answerType: "single_select", required: true,
    options: [{ id: newId(), label: "", attached_tags: [] }, { id: newId(), label: "", attached_tags: [] }],
  }] });

  const removeQuestion = (i: number) => {
    setCfg({ ...cfg, questions: qs.filter((_, n) => n !== i) });
  };

  const addTagToOption = (qIdx: number, optId: string, rawTag: string) => {
    const cleaned = rawTag.trim().toLowerCase().replace(/^#/, "").replace(/[^a-z0-9_-]/g, "-");
    if (!cleaned) return;
    const q = qs[qIdx];
    const nextOpts = q.options.map((o) => {
      if (o.id !== optId) return o;
      const cur = o.attached_tags ?? [];
      if (cur.includes(cleaned)) return o;
      return { ...o, attached_tags: [...cur, cleaned] };
    });
    patchQ(qIdx, { options: nextOpts });
  };

  const removeTagFromOption = (qIdx: number, optId: string, tagToRemove: string) => {
    const q = qs[qIdx];
    const nextOpts = q.options.map((o) => {
      if (o.id !== optId) return o;
      return { ...o, attached_tags: (o.attached_tags ?? []).filter((t) => t !== tagToRemove) };
    });
    patchQ(qIdx, { options: nextOpts });
  };

  const saveAndGo = async (tab: TabKey) => {
    if (!dirty || (await save())) go(tab);
  };
  const blankOptions = qs.some((q) => q.answerType !== "short_text" && q.options.some((o) => !o.label.trim()));

  return (
    <div className="stack" style={{ maxWidth: 860 }}>
      {params.get("new") && (
        <div className="ok-box">Demo created. Next, an optional intake survey routes each viewer to the version of the tour that fits them. Skip it for a simple click-through.</div>
      )}
      <div className="panel stack">
        <label className="check">
          <input type="checkbox" checked={cfg.surveyEnabled} onChange={(e) => setCfg({ ...cfg, surveyEnabled: e.target.checked })} />
          <b>Ask viewers a short survey before the demo</b>
        </label>
        <p className="small muted">
          Answers attach tags to the viewer (e.g. <code>24-7-coverage</code>, <code>understaffed</code>).
          Each viewer then sees the path whose routing tags (set under Paths) they match the most of, or the default path.
        </p>
        {cfg.surveyEnabled && (
          <label className="field"><span>Intro text <span className="hint">Shown above the first question</span></span>
            <input type="text" value={cfg.surveyIntro} onChange={(e) => setCfg({ ...cfg, surveyIntro: e.target.value })} />
          </label>
        )}
      </div>

      {qs.length === 0 && (
        <div className="panel stack-s">
          <h3>Start from the standard survey</h3>
          <p className="small muted">Five questions pre-configured with functional routing tags. Edit anything after loading, or skip the survey below for a simple click-through.</p>
          <div className="row">
            <button className="btn btn-primary" onClick={() => setCfg(starterSurvey(cfg))}>Load standard survey</button>
            <button className="btn" onClick={addQuestion}>Start blank</button>
          </div>
        </div>
      )}

      {cfg.surveyEnabled && qs.map((q, i) => (
        <div key={q.id} className="q-card">
          <div className="q-head">
            <span className="q-num">{i + 1}</span>
            <input type="text" placeholder="Question text" value={q.prompt} onChange={(e) => patchQ(i, { prompt: e.target.value })} aria-label={`Question ${i + 1} text`} />
            <button className="btn btn-sm btn-ghost" title="Move up" aria-label="Move up" onClick={() => setQs(move(qs, i, -1))} disabled={i === 0}>↑</button>
            <button className="btn btn-sm btn-ghost" title="Move down" aria-label="Move down" onClick={() => setQs(move(qs, i, 1))} disabled={i === qs.length - 1}>↓</button>
            <button className="btn btn-sm btn-danger" onClick={() => removeQuestion(i)}>Remove</button>
          </div>
          <div className="row" style={{ paddingLeft: 32 }}>
            <select value={q.answerType} onChange={(e) => patchQ(i, { answerType: e.target.value })} style={{ width: "auto" }} aria-label="Answer type">
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label className="check"><input type="checkbox" checked={q.required} onChange={(e) => patchQ(i, { required: e.target.checked })} />Required</label>
          </div>
          {q.answerType !== "short_text" && (
            <div className="stack-s" style={{ paddingLeft: 32 }}>
              {q.options.map((o, oi) => (
                <div key={o.id} className="opt-block" style={{ padding: "8px 10px", background: "var(--paper)", borderRadius: "var(--r-s)" }}>
                  <div className="opt-row">
                    <input type="text" placeholder={`Answer ${oi + 1}`} value={o.label}
                      onChange={(e) => patchQ(i, { options: q.options.map((x) => (x.id === o.id ? { ...x, label: e.target.value } : x)) })} />
                    <button className="btn btn-sm btn-ghost" aria-label="Move answer up" onClick={() => patchQ(i, { options: move(q.options, oi, -1) })} disabled={oi === 0}>↑</button>
                    <button className="btn btn-sm btn-ghost" aria-label="Remove answer" onClick={() => {
                      patchQ(i, { options: q.options.filter((x) => x.id !== o.id) });
                    }} disabled={q.options.length <= 2}>✕</button>
                  </div>
                  <div className="opt-tags-row" style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap", marginTop: "6px" }}>
                    <span className="small muted" style={{ fontSize: "11px" }}>Tags:</span>
                    {(o.attached_tags ?? []).map((tag) => (
                      <span key={tag} className="tag-badge" style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px", background: "var(--accent-wash)", color: "var(--accent-ink)", borderRadius: "12px", fontSize: "12px", fontWeight: 500 }}>
                        #{tag}
                        <button type="button" aria-label={`Remove tag ${tag}`} style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "inherit", fontSize: "11px", lineHeight: 1 }} onClick={() => removeTagFromOption(i, o.id, tag)}>✕</button>
                      </span>
                    ))}
                    <TagAutocompleteInput
                      compact
                      activeTags={o.attached_tags ?? []}
                      allSuggestions={allKnownTags}
                      onAddTag={(tag) => addTagToOption(i, o.id, tag)}
                      placeholder="+ Add tag (Enter)"
                      inputClassName="tag-add-input"
                      inputStyle={{ height: "24px", width: "130px", fontSize: "11px", padding: "0 6px" }}
                    />
                  </div>
                </div>
              ))}
              <div><button className="btn btn-sm" onClick={() => patchQ(i, { options: [...q.options, { id: newId(), label: "", attached_tags: [] }] })} disabled={q.options.length >= 12}>Add answer</button></div>
            </div>
          )}
          {q.answerType === "short_text" && <p className="small muted" style={{ paddingLeft: 32 }}>Text answers show up in analytics but don't affect routing.</p>}
        </div>
      ))}

      {cfg.surveyEnabled && qs.length > 0 && (
        <div><button className="btn" onClick={addQuestion} disabled={qs.length >= 15}>Add question</button></div>
      )}

      {msg && <div className={msg.ok ? "ok-box" : "error-box"}>{msg.text}</div>}
      {blankOptions && <div className="error-box">Fill in or remove empty answers before saving.</div>}

      <div className="tab-foot">
        <button className="btn btn-ghost" onClick={() => go("studio")}>Back to Studio</button>
        <span className="spacer" />
        <span className={`status-line ${dirty ? "dirty" : ""}`}>{dirty ? "Unsaved changes" : ""}</span>
        <button className="btn" onClick={save} disabled={!dirty || saving || blankOptions}>{saving ? "Saving…" : "Save"}</button>
        {cfg.surveyEnabled
          ? <button className="btn btn-primary" disabled={saving || blankOptions} onClick={() => saveAndGo("preview")}>Save and continue to Preview</button>
          : <button className="btn btn-primary" disabled={saving} onClick={() => saveAndGo("preview")}>Skip survey, continue to Preview</button>}
      </div>
    </div>
  );
}
